<template>
  <div class="report-filters-bar">
  <div v-if="has('period')" class="period-segment mr-3" role="tablist">
    <button
      v-for="chip in periodChips"
      :key="chip.value"
      type="button"
      role="tab"
      :aria-selected="String(periodChip === chip.value)"
      class="period-segment__item"
      :class="{ 'period-segment__item--active': periodChip === chip.value }"
      @click="onPeriodChip(chip.value)"
    >
      {{ chip.text }}
    </button>
  </div>

  <v-menu v-if="has('period')" offset-y min-width="170" content-class="period-menu">
    <template v-slot:activator="{ on, attrs }">
      <button type="button" class="period-mobile" v-bind="attrs" v-on="on">
        {{ periodLabel }}
        <v-icon x-small color="white" class="ml-1">mdi-chevron-down</v-icon>
      </button>
    </template>
    <v-list dense>
      <v-list-item
        v-for="chip in periodChips"
        :key="chip.value"
        :input-value="periodChip === chip.value"
        @click="onPeriodChip(chip.value)"
      >
        <v-list-item-title>{{ chip.text }}</v-list-item-title>
        <v-list-item-icon v-if="periodChip === chip.value" class="ml-2 my-auto">
          <v-icon small color="primary">mdi-check</v-icon>
        </v-list-item-icon>
      </v-list-item>
    </v-list>
  </v-menu>

  <filter-menu
    title="Filtrar reporte"
    :current-filter="countFilter"
    :disable-filter-button="customRangeIncomplete"
    @canceled="rollback()"
    @filtered="commit()"
    @showed="loadOptions()"
  >
    <div class="report-filters">
      <v-autocomplete
        v-if="dealerItems.length"
        v-model="filter.dealer_id"
        :items="dealerItems"
        item-text="name"
        item-value="id"
        label="Empresa"
        prepend-inner-icon="mdi-domain"
        placeholder="Todas las del grupo"
        persistent-placeholder
        multiple
        chips
        small-chips
        deletable-chips
        clearable
        outlined
        dense
        hide-details="auto"
        class="mb-4"
      ></v-autocomplete>

      <v-select
        v-if="has('period')"
        v-model="filter.period"
        :items="periodItems"
        label="Periodo"
        prepend-inner-icon="mdi-calendar"
        outlined
        dense
        hide-details="auto"
        class="mb-4"
      ></v-select>

      <div v-if="has('period') && filter.period === 'custom'" class="mb-4">
        <date-range-picker :return-value.sync="dateRange" edit-range label="Rango de fechas"></date-range-picker>
      </div>

      <v-select
        v-if="has('type')"
        v-model="filter.type"
        :items="typeItems"
        label="Tipo"
        prepend-inner-icon="mdi-swap-horizontal"
        clearable
        outlined
        dense
        hide-details="auto"
        class="mb-4"
      ></v-select>

      <v-select
        v-if="has('condition')"
        v-model="filter.condition"
        :items="conditionItems"
        label="Condición"
        prepend-inner-icon="mdi-car-info"
        multiple
        clearable
        outlined
        dense
        hide-details="auto"
        class="mb-4"
      ></v-select>

      <v-autocomplete
        v-if="has('location_id')"
        v-model="filter.location_id"
        :items="locationItems"
        :loading="loadingLocations"
        item-text="name"
        item-value="id"
        label="Ubicación"
        prepend-inner-icon="mdi-map-marker"
        multiple
        chips
        small-chips
        deletable-chips
        clearable
        outlined
        dense
        hide-details="auto"
        class="mb-4"
      ></v-autocomplete>

      <v-autocomplete
        v-if="has('responsible_id')"
        v-model="filter.responsible_id"
        :items="responsibleItems"
        :loading="loadingOptions"
        item-text="name"
        item-value="id"
        label="Responsable"
        prepend-inner-icon="mdi-account"
        clearable
        outlined
        dense
        hide-details="auto"
        class="mb-4"
      ></v-autocomplete>

      <v-autocomplete
        v-if="has('created_by')"
        v-model="filter.created_by"
        :items="creatorItems"
        :loading="loadingOptions"
        item-text="name"
        item-value="id"
        label="Creado por"
        prepend-inner-icon="mdi-account-edit"
        clearable
        outlined
        dense
        hide-details="auto"
        class="mb-4"
      ></v-autocomplete>

      <v-text-field
        v-if="has('inactivity_threshold')"
        v-model.number="filter.inactivity_threshold"
        type="number"
        min="1"
        label="Umbral de inactividad (días)"
        prepend-inner-icon="mdi-timer-sand"
        outlined
        dense
        hide-details="auto"
        class="mb-4"
      ></v-text-field>

      <v-text-field
        v-if="has('review_threshold')"
        v-model.number="filter.review_threshold"
        type="number"
        min="1"
        label="Umbral de revisión (días)"
        prepend-inner-icon="mdi-timer-sand"
        outlined
        dense
        hide-details="auto"
        class="mb-4"
      ></v-text-field>
    </div>
  </filter-menu>
  </div>
</template>

<script>
import FilterMenu from './FilterMenu'
import DateRangePicker from './DateRangePicker'
import LocationService from '../services/LocationService'
import ReportService from '../services/ReportService'

const DEFAULT_THRESHOLD = 30
const DEFAULT_PERIOD = 'this_month'

const emptyFilter = () => ({
  period: DEFAULT_PERIOD,
  start_date: null,
  end_date: null,
  dealer_id: [],
  location_id: [],
  condition: [],
  type: null,
  responsible_id: null,
  created_by: null,
  inactivity_threshold: DEFAULT_THRESHOLD,
  review_threshold: DEFAULT_THRESHOLD
})

// Filtros de los reportes en un menú como el del resto de vistas.
// `fields` indica qué filtros admite cada reporte; al aplicar emite `change` con los parámetros
// listos para el servicio (solo los que tienen valor).
export default {
  name: 'report-filters',

  components: { FilterMenu, DateRangePicker },

  props: {
    fields: {
      type: Array,
      required: true
    }
  },

  data: () => ({
    filter: emptyFilter(),
    applied: emptyFilter(),
    dateRange: [],

    periodItems: [
      { text: 'Este mes', value: 'this_month' },
      { text: 'Mes anterior', value: 'last_month' },
      { text: 'Últimos 90 días', value: 'last_90' },
      { text: 'Personalizado', value: 'custom' },
      { text: 'Todo el histórico', value: 'all' }
    ],
    // Atajos de periodo visibles en la barra; "Personalizado" sigue en el menú
    periodChips: [
      { text: 'Este mes', value: 'this_month' },
      { text: 'Mes anterior', value: 'last_month' },
      { text: '90 días', value: 'last_90' },
      { text: 'Histórico', value: 'all' }
    ],
    conditionItems: [
      { text: 'VN', value: 'new' },
      { text: 'VO', value: 'used' },
      { text: 'Taller', value: 'taller' }
    ],
    typeItems: [
      { text: 'Movimientos', value: 'movements' },
      { text: 'Cesiones', value: 'assignments' },
      { text: 'Ambos', value: 'both' }
    ],

    locationItems: [],
    dealerItems: [],
    responsibleItems: [],
    creatorItems: [],
    loadingLocations: false,
    loadingOptions: false,
    optionsLoaded: false
  }),

  computed: {
    // Lo que ve el badge de FilterMenu: solo filtros aplicados que se salen del valor por defecto
    countFilter () {
      const a = this.applied
      return {
        period: this.has('period') && a.period !== DEFAULT_PERIOD ? a.period : null,
        dealer_id: a.dealer_id,
        location_id: a.location_id,
        condition: a.condition,
        type: a.type && a.type !== 'both' ? a.type : null,
        responsible_id: a.responsible_id,
        created_by: a.created_by,
        inactivity_threshold: a.inactivity_threshold !== DEFAULT_THRESHOLD ? a.inactivity_threshold : null,
        review_threshold: a.review_threshold !== DEFAULT_THRESHOLD ? a.review_threshold : null
      }
    },

    // Chip activo = periodo aplicado (ninguno si es personalizado)
    periodChip () {
      return this.periodChips.some(c => c.value === this.applied.period) ? this.applied.period : undefined
    },

    periodLabel () {
      const chip = this.periodChips.find(c => c.value === this.applied.period)
      return chip ? chip.text : 'Personalizado'
    },

    // Con una sola fecha el backend deja el otro extremo abierto, pero hace falta al menos una
    customRangeIncomplete () {
      return this.has('period') && this.filter.period === 'custom' && !(this.filter.start_date || this.filter.end_date)
    },

    // Parámetros que condicionan las opciones de Responsable / Creado por
    optionsKey () {
      const f = this.filter
      return JSON.stringify([this.has('period') ? f.period : null, f.start_date, f.end_date, f.dealer_id])
    }
  },

  watch: {
    // Las opciones dependen del periodo y de las empresas elegidas: se recargan al cambiarlos
    optionsKey () {
      if (this.optionsLoaded) this.loadReportOptions()
    },
    dateRange (val) {
      if (val && val.length === 2) {
        const sorted = [...val].sort()
        this.filter.start_date = sorted[0]
        this.filter.end_date = sorted[1]
      } else {
        this.filter.start_date = null
        this.filter.end_date = null
      }
    },
    'filter.period' (val) {
      if (val !== 'custom') {
        this.dateRange = []
      }
    }
  },

  methods: {
    has (field) {
      return this.fields.includes(field)
    },

    // Un chip aplica el periodo al instante; deseleccionar el activo no hace nada
    onPeriodChip (value) {
      if (value === this.applied.period) return
      this.filter.period = value
      this.commit()
    },

    buildParams () {
      const f = this.applied
      const params = {}
      if (this.has('period') && f.period) {
        params.period = f.period
        if (f.period === 'custom') {
          if (f.start_date) params.start_date = f.start_date
          if (f.end_date) params.end_date = f.end_date
        }
      }
      if (f.dealer_id.length) params.dealer_id = f.dealer_id
      if (this.has('location_id') && f.location_id.length) params.location_id = f.location_id
      if (this.has('condition') && f.condition.length) params.condition = f.condition.join(',')
      if (this.has('type') && f.type) params.type = f.type
      if (this.has('responsible_id') && f.responsible_id) params.responsible_id = f.responsible_id
      if (this.has('created_by') && f.created_by) params.created_by = f.created_by
      if (this.has('inactivity_threshold') && f.inactivity_threshold) params.inactivity_threshold = f.inactivity_threshold
      if (this.has('review_threshold') && f.review_threshold) params.review_threshold = f.review_threshold
      return params
    },

    commit () {
      // console.log('[ReportFilters] commit, filter:', JSON.stringify(this.filter))
      this.applied = this.cloneFilter(this.filter)
      this.$emit('change', this.buildParams())
    },

    cloneFilter (f) {
      return { ...f, dealer_id: [...f.dealer_id], location_id: [...f.location_id], condition: [...f.condition] }
    },

    rollback () {
      // console.log('[ReportFilters] rollback (menú cerrado sin aplicar)')
      this.filter = this.cloneFilter(this.applied)
      this.dateRange = this.applied.start_date && this.applied.end_date ? [this.applied.start_date, this.applied.end_date] : []
    },

    async loadOptions () {
      if (this.optionsLoaded) return
      this.optionsLoaded = true
      if (this.has('location_id')) {
        try {
          this.loadingLocations = true
          const { data } = await LocationService.getAll()
          this.locationItems = data.data || []
        } catch (error) {
          this.optionsLoaded = false
          this.$toast.error('Error al cargar las ubicaciones')
        } finally {
          this.loadingLocations = false
        }
      }
      await this.loadReportOptions()
    },

    // Empresas (solo admin de grupo), responsables y creadores según periodo y empresas elegidas
    async loadReportOptions () {
      const f = this.filter
      const params = {}
      if (this.has('period') && f.period) {
        params.period = f.period
        if (f.period === 'custom') {
          if (f.start_date) params.start_date = f.start_date
          if (f.end_date) params.end_date = f.end_date
        }
      }
      if (f.dealer_id.length) params.dealer_id = f.dealer_id

      const requestKey = this.optionsKey
      try {
        this.loadingOptions = true
        const { data } = await ReportService.getReportFilters(params)
        // Si el usuario cambió los filtros mientras tanto, esta respuesta ya no vale
        if (requestKey !== this.optionsKey) return
        const options = data.data || {}
        this.dealerItems = options.dealers || []
        this.responsibleItems = options.responsibles || []
        this.creatorItems = options.creators || []
        // Una selección que ya no está entre las opciones se descarta
        if (this.filter.responsible_id && !this.responsibleItems.some(o => o.id === this.filter.responsible_id)) {
          this.filter.responsible_id = null
        }
        if (this.filter.created_by && !this.creatorItems.some(o => o.id === this.filter.created_by)) {
          this.filter.created_by = null
        }
      } catch (error) {
        this.optionsLoaded = false
        this.$toast.error('Error al cargar las opciones de los filtros')
      } finally {
        this.loadingOptions = false
      }
    }
  }
}
</script>

<style scoped>
.report-filters-bar {
  display: flex;
  align-items: center;
}
.period-mobile {
  display: none;
}
/* Liquid glass: vidrio translúcido con desenfoque, brillos especulares y segmento activo también de vidrio */
.period-segment {
  position: relative;
  display: inline-flex;
  padding: 4px;
  border-radius: 999px;
  background: transparent;
  box-shadow:
    inset 0 1px 1px rgba(255, 255, 255, 0.7),
    inset 0 -1px 1px rgba(255, 255, 255, 0.25),
    inset 0 0 0 1px rgba(255, 255, 255, 0.28),
    0 4px 14px rgba(0, 0, 0, 0.12);
}
.period-segment__item {
  position: relative;
  padding: 5px 14px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: rgba(255, 255, 255, 0.82);
  font-size: 13px;
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  outline: none;
  transition: background 0.3s ease, color 0.3s ease, box-shadow 0.3s ease, transform 0.2s ease;
}
.period-segment__item:hover:not(.period-segment__item--active) {
  color: #fff;
  background: rgba(255, 255, 255, 0.12);
}
.period-segment__item:active {
  transform: scale(0.96);
}
.period-segment__item:focus-visible {
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.7);
}
.period-segment__item--active {
  color: #fff;
  font-weight: 600;
  background: transparent;
  box-shadow:
    inset 0 1px 1px rgba(255, 255, 255, 0.9),
    inset 0 -1px 2px rgba(255, 255, 255, 0.35),
    inset 0 0 0 1px rgba(255, 255, 255, 0.35),
    0 2px 8px rgba(0, 0, 0, 0.15);
}
.report-filters {
  min-width: 340px;
}
</style>

<style>
/* Móvil: la barra del reporte admite una segunda fila para la píldora de periodo */
@media (max-width: 599px) {
  .report-toolbar.v-toolbar {
    height: auto !important;
  }
  .report-toolbar .v-toolbar__content {
    flex-wrap: wrap;
    height: auto !important;
    padding-top: 4px;
    padding-bottom: 10px;
  }
  .report-toolbar .v-toolbar__title {
    flex: 0 0 calc(100% - 32px);
    min-width: 0;
    white-space: normal;
    line-height: 1.25;
    font-size: 1.05rem;
  }
  .report-toolbar .v-btn {
    min-width: 0 !important;
    padding: 0 10px !important;
  }
  .report-toolbar .v-badge {
    margin-left: 2px !important;
    margin-right: 2px !important;
  }
  .report-toolbar .v-btn.ml-2 {
    margin-left: 6px !important;
  }
  .report-toolbar .v-btn .v-icon--left {
    margin-right: 0;
  }
  .report-toolbar .report-filters-bar {
    display: contents;
  }
  .report-toolbar .report-filters-bar .period-segment {
    display: none;
  }
  .report-toolbar .report-filters-bar .period-mobile {
    display: inline-flex;
    align-items: center;
    margin-right: 4px;
    padding: 6px 10px;
    border: 0;
    border-radius: 999px;
    background: transparent;
    color: #fff;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
    cursor: pointer;
    outline: none;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
    box-shadow:
      inset 0 1px 1px rgba(255, 255, 255, 0.7),
      inset 0 -1px 1px rgba(255, 255, 255, 0.25),
      inset 0 0 0 1px rgba(255, 255, 255, 0.3),
      0 2px 8px rgba(0, 0, 0, 0.15);
  }
}
</style>
